import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  AccountBalance as CaixaIcon,
  Assessment as DreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import saleService from '../services/saleService';
import { EXPENSE_CATEGORIES, ExpenseCategory } from '../types';
import { PieChartComponent, MonthlyBarChart } from '../components/charts';
import { getCashRecognizedProfitFromSale } from '../utils/saleRealProfit';

const ensureNumber = (value: unknown): number => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const cleanValue = value.replace(/[^\d.-]/g, '');
    return parseFloat(cleanValue) || 0;
  }
  return 0;
};

const formatCurrency = (value: number): string =>
  ensureNumber(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const kpiCardSx = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
};

type DashboardData = {
  totalVehicles: number;
  totalExpenses: number;
  totalSales: number;
  totalProfit: number;
  expensesByCategory: {
    labels: string[];
    data: number[];
  };
  salesByMonth: Array<{ month: string; total: number; count: number }>;
};

const emptyDashboard: DashboardData = {
  totalVehicles: 0,
  totalExpenses: 0,
  totalSales: 0,
  totalProfit: 0,
  expensesByCategory: { labels: [], data: [] },
  salesByMonth: [],
};

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noData, setNoData] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData>(emptyDashboard);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoData(false);

    try {
      const dashboard = await saleService.getSummary();

      const hasVehicles = dashboard?.totalVehicles > 0;
      const hasExpenses = dashboard?.byCategory?.length > 0 || dashboard?.expensesByCategory?.length > 0;
      const hasSales = dashboard?.totalSales > 0 || (dashboard?.salesByMonth?.length ?? 0) > 0;

      if (!hasVehicles && !hasExpenses && !hasSales) {
        setNoData(true);
        setDashboardData(emptyDashboard);
        return;
      }

      const expenseCategories = (dashboard.expensesByCategory || []).map((item: { category: string }) => {
        const category = item.category as ExpenseCategory;
        return EXPENSE_CATEGORIES[category] || 'Outro';
      });

      const expensesData = (dashboard.expensesByCategory || []).map(
        (item: { amount: unknown }) => ensureNumber(item.amount)
      );

      const salesByMonth = (dashboard.salesByMonth || []).map(
        (item: { month: string; total: number; count: number }) => ({
          month: item.month || '',
          total: ensureNumber(item.total),
          count: ensureNumber(item.count),
        })
      );

      let totalProfit = 0;
      try {
        const rawSales = await saleService.getAllSales();
        const sales = await saleService.enrichTermSalesWithInstallments(rawSales || []);
        totalProfit = sales.reduce((sum, sale) => sum + getCashRecognizedProfitFromSale(sale), 0);
      } catch {
        totalProfit = ensureNumber(dashboard.totalProfit);
      }

      const totalSalesCount = salesByMonth.reduce(
        (acc: number, curr: { count: number }) => acc + ensureNumber(curr.count),
        0
      );

      let totalExpensesValue = ensureNumber(dashboard.totalExpenses);
      if (totalExpensesValue > 1_000_000 || Number.isNaN(totalExpensesValue)) {
        totalExpensesValue = (dashboard.byCategory || []).reduce(
          (sum: number, item: { amount: unknown }) => sum + ensureNumber(item.amount),
          0
        );
      }

      setDashboardData({
        totalVehicles: ensureNumber(dashboard.totalVehicles),
        totalExpenses: totalExpensesValue,
        totalSales: totalSalesCount || ensureNumber(dashboard.totalSales),
        totalProfit,
        expensesByCategory: {
          labels: expenseCategories.length > 0 ? expenseCategories : ['Sem dados'],
          data: expensesData.length > 0 ? expensesData : [0],
        },
        salesByMonth,
      });
    } catch (err) {
      console.error('Erro ao carregar dados do dashboard:', err);
      setError('Não foi possível carregar os dados do dashboard. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading && dashboardData.totalVehicles === 0 && !error && !noData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !dashboardData.totalSales && !dashboardData.totalVehicles) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void fetchDashboardData()}>
          Tentar novamente
        </Button>
      </Box>
    );
  }

  if (noData) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 0.2, mb: 1 }}>
          Dashboard
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Ainda não há dados para exibir
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
            Cadastre veículos, registre despesas e vendas para acompanhar o desempenho da loja aqui.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<CarIcon />} onClick={() => navigate('/veiculos/novo')}>
              Cadastrar veículo
            </Button>
            <Button variant="outlined" startIcon={<MoneyIcon />} onClick={() => navigate('/despesas/nova')}>
              Registrar despesa
            </Button>
            <Button variant="outlined" startIcon={<ReceiptIcon />} onClick={() => navigate('/vendas/novo')}>
              Registrar venda
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const profitColor =
    dashboardData.totalProfit >= 0 ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Visão geral da operação. O lucro considera o regime de caixa: vendas a prazo entram conforme entrada e
            baixa das parcelas.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => void fetchDashboardData()}
            disabled={loading}
          >
            {loading ? 'Atualizando...' : 'Atualizar'}
          </Button>
          <Button variant="outlined" startIcon={<CaixaIcon />} onClick={() => navigate('/caixa')}>
            Caixa
          </Button>
          <Button variant="outlined" startIcon={<DreIcon />} onClick={() => navigate('/dre')}>
            DRE
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              ...kpiCardSx,
              minHeight: { xs: 160, md: 180 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              background:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(145deg, rgba(46,125,50,0.12) 0%, rgba(0,0,0,0) 70%)'
                  : 'linear-gradient(145deg, rgba(46,125,50,0.08) 0%, rgba(255,255,255,0) 70%)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Lucro no caixa
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mt: 0.5,
                    color: profitColor,
                    fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.5rem' },
                    lineHeight: 1.15,
                    wordBreak: 'break-word',
                  }}
                >
                  {formatCurrency(dashboardData.totalProfit)}
                </Typography>
              </Box>
              <TrendingUpIcon sx={{ color: profitColor, mt: 0.25 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Resultado reconhecido conforme pagamentos recebidos.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Grid container spacing={2.5} sx={{ height: '100%' }}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={kpiCardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary">
                      Veículos
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {dashboardData.totalVehicles}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Em estoque / frota
                    </Typography>
                  </Box>
                  <CarIcon sx={{ color: theme.palette.primary.main, mt: 0.25, flexShrink: 0 }} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={kpiCardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary">
                      Vendas
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {dashboardData.totalSales}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Quantidade registrada
                    </Typography>
                  </Box>
                  <ReceiptIcon sx={{ color: theme.palette.success.main, mt: 0.25, flexShrink: 0 }} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={kpiCardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary">
                      Despesas
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mt: 0.5,
                        color: theme.palette.error.main,
                        fontSize: { xs: '1.25rem', sm: '1.35rem' },
                        wordBreak: 'break-word',
                      }}
                    >
                      {formatCurrency(dashboardData.totalExpenses)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Total acumulado
                    </Typography>
                  </Box>
                  <MoneyIcon sx={{ color: theme.palette.error.main, mt: 0.25, flexShrink: 0 }} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ ...kpiCardSx, p: 0, overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Despesas por categoria
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Distribuição do gasto registrado.
              </Typography>
            </Box>
            <Box sx={{ px: 1, pb: 1 }}>
              <PieChartComponent
                title=""
                embedded
                data={{
                  labels: dashboardData.expensesByCategory.labels,
                  values: dashboardData.expensesByCategory.data,
                }}
                height={300}
                loading={loading}
                error={null}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ ...kpiCardSx, p: 0, overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Vendas por mês
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Faturamento mensal das vendas.
              </Typography>
            </Box>
            <Box sx={{ px: 1, pb: 1 }}>
              <MonthlyBarChart
                title=""
                embedded
                data={dashboardData.salesByMonth}
                height={300}
                loading={loading}
                error={null}
                color={theme.palette.primary.main}
                valueLabel="Vendas (R$)"
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={{ ...kpiCardSx, py: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Atalhos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Acesso rápido às principais áreas financeiras.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" onClick={() => navigate('/vendas')}>
                  Vendas
                </Button>
                <Button size="small" variant="outlined" onClick={() => navigate('/despesas')}>
                  Despesas
                </Button>
                <Button size="small" variant="outlined" onClick={() => navigate('/veiculos')}>
                  Veículos
                </Button>
                <Button size="small" variant="outlined" onClick={() => navigate('/movimentacoes')}>
                  Movimentações
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
